import React from 'react';
import { Link, Navigate, matchPath, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/api';
import { usePageConfig } from '@/contexts/PageConfigContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useLeague } from '@/hooks/useLeague';
import { leagueTabPath, useLeagueTabs, type LeagueTab } from '@/hooks/useLeagueTabs';
import { ContentLayout } from '@/layouts/ContentLayout';
import Error404 from '@/components/Error404';
import { TournamentInfoCard } from '@/components/TournamentInfoCard';
import { PillButton } from '@/components/ui/PillButton';
import LeagueMatchesTab from '@/pages/league-detail/MatchesTab';
import LeaguePlayoffTab from '@/pages/league-detail/PlayoffTab';
import LeagueStandingsTab from '@/pages/league-detail/StandingsTab';
import LeagueTournamentsTab from '@/pages/league-detail/TournamentsTab';
import type { LeagueTournamentsResponse } from '@/types/league';
import styles from '@/pages/LeagueDetailPage.module.scss';
import { LeagueDetailContext } from './context';

const TAB_COMPONENTS: Record<LeagueTab, React.FC> = {
  matches: LeagueMatchesTab,
  standings: LeagueStandingsTab,
  playoff: LeaguePlayoffTab,
  tournaments: LeagueTournamentsTab,
};

/**
 * League detail layout (`/matches/:leagueId/*`).
 *
 * Tabs: Matches (default) | Table | Play-off | Tournaments. Which of them a
 * league has, and their order, comes from `useLeagueTabs`; the address of a
 * tab the league does not have opens "Matches".
 *
 * Deliberately free of auth-dependent hooks — the league read endpoints are
 * public, so moving this route outside <ProtectedRoute> later is a pure
 * routing change. The sidebar's tournaments query is the one authenticated
 * call; it degrades to an empty block when it fails.
 */
const LeagueDetailLayout: React.FC = () => {
  const { leagueId = '' } = useParams<{ leagueId: string }>();
  const { pathname } = useLocation();
  const { isDesktop } = useBreakpoint();
  const { t } = useTranslation(['league', 'tournament']);

  const { data: league, isLoading, error } = useLeague(leagueId);
  const { tabs: leagueTabs, isSettled } = useLeagueTabs(leagueId);

  // Shares its cache with the Tournaments tab via the same queryKey.
  const { data: leagueTournaments } = useQuery<LeagueTournamentsResponse>({
    queryKey: ['tournaments-by-league', leagueId],
    queryFn: async () => (await apiClient.getLeagueTournaments(leagueId)).data.data,
    enabled: !!leagueId,
  });

  // While the name is unknown (first paint) pass a single space so the header
  // reserves its space without showing a placeholder string.
  usePageConfig({
    title: league?.name || ' ',
    headerSolid: true,
  });

  const tabs = React.useMemo(
    () => leagueTabs.map(tab => ({ component: TAB_COMPONENTS[tab], path: leagueTabPath(leagueId, tab) })),
    [leagueTabs, leagueId],
  );

  if (isLoading) return null;

  if (error) {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    if (status === 404 || status === 400) return <Error404 />;
    return null;
  }

  // The tab list is final before anything renders, so no tab appears under the user later.
  if (!league || !leagueId || !isSettled) return null;

  if (!tabs.some(tab => matchPath(tab.path, pathname))) {
    return <Navigate to={leagueTabPath(leagueId, 'matches')} replace />;
  }

  const myTournaments = leagueTournaments?.mine ?? [];

  const sidebar = (
    <>
      <TournamentInfoCard
        logoUrl={league.logoUrl || undefined}
        logoAlt={league.name}
        title={league.name}
        subtitle={[league.country, league.season].filter(Boolean).join(' · ')}
        stats={typeof league.matchCount === 'number'
          ? [{ label: t('league:matches'), value: league.matchCount }]
          : []}
        statLayout="row"
        collapsibleOnMobile
      />

      {isDesktop && (
        <PillButton
          to={`/tournaments/create?leagueId=${leagueId}`}
          label={t('league:createTournamentInLeague')}
        />
      )}

      {isDesktop && myTournaments.length > 0 && (
        <div className={styles.sidebarTournaments}>
          <h3 className={styles.groupTitle}>{t('league:myTournamentsInLeague')}</h3>
          {myTournaments.map((tournament) => (
            <Link
              key={tournament.id}
              to={`/tournaments/${tournament.id}`}
              className={styles.sidebarTournamentRow}
            >
              {tournament.leagueLogo && (
                <img
                  src={tournament.leagueLogo}
                  alt=""
                  width={24}
                  height={24}
                  decoding="async"
                />
              )}
              <span>{tournament.name}</span>
              <i className="icon-chevron-right" aria-hidden="true" />
            </Link>
          ))}
        </div>
      )}
    </>
  );

  return (
    <LeagueDetailContext.Provider value={{ league, leagueId }}>
      <ContentLayout sidebar={sidebar} tabs={tabs} />
    </LeagueDetailContext.Provider>
  );
};

export default LeagueDetailLayout;
